import {
  type ErrorTip,
  ErrorTipMode,
  type SuccessTip,
  SuccessTipMode,
} from '@ts-max/axios';
import { Dialog, Toast } from 'antd-mobile';
import { t } from 'i18next';
import { isValidElement, type ReactNode } from 'react';

type TipPayload<Mode> = {
  content?: ReactNode | string;
  mode: Mode;
  title: ReactNode | string;
};

function isRenderableElement(value: unknown): value is ReactNode {
  return isValidElement(value);
}

function normalizeSuccessTip(
  options: SuccessTip | string | ReactNode,
): TipPayload<SuccessTipMode> | null {
  if (!options) return null;

  const fallbackTitle = t('common:success');

  if (typeof options === 'string' || isRenderableElement(options)) {
    return {
      content: options,
      mode: SuccessTipMode.message,
      title: fallbackTitle,
    };
  }

  return {
    content: options.content,
    mode: options.mode || SuccessTipMode.message,
    title: options.title || fallbackTitle,
  };
}

function normalizeErrorTip(
  options: ErrorTip | string | ReactNode | Error,
): TipPayload<ErrorTipMode> | null {
  if (!options) return null;

  const fallbackTitle = t('common:error');

  if (options instanceof Error) {
    return {
      content: options.message,
      mode: ErrorTipMode.message,
      title: fallbackTitle,
    };
  }

  if (typeof options === 'string' || isRenderableElement(options)) {
    return {
      content: options,
      mode: ErrorTipMode.message,
      title: fallbackTitle,
    };
  }

  return {
    content: options.content,
    mode: options.mode || ErrorTipMode.message,
    title: options.title || fallbackTitle,
  };
}

function showDialog(payload: TipPayload<SuccessTipMode | ErrorTipMode>) {
  if (!payload.content) return;

  void Dialog.alert({
    closeOnMaskClick: true,
    confirmText: t('common:acknowledged'),
    content: payload.content,
    title: payload.title,
  });
}

export function showMobileSuccess(options: SuccessTip | string | ReactNode) {
  const payload = normalizeSuccessTip(options);
  if (!payload?.content) return;

  if (payload.mode === SuccessTipMode.message) {
    Toast.show({
      content: payload.content,
      icon: 'success',
    });
    return;
  }

  showDialog(payload);
}

export function showMobileError(
  options: ErrorTip | string | ReactNode | Error,
) {
  const payload = normalizeErrorTip(options);
  if (!payload?.content) return;

  if (payload.mode === ErrorTipMode.message) {
    Toast.show({
      content: payload.content,
      icon: 'fail',
    });
    return;
  }

  showDialog(payload);
}

export function clearMobileFeedback() {
  Toast.clear();
  Dialog.clear();
}
